import type { LoginResultMVC } from "@repo/baseCloud";
import type { FormEvent } from "react";

import { AccountController } from "@repo/baseCloud";
import { log, useSearchParams } from "@repo/helpers";
import { LoginPage } from "@repo/ui";
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { AuthContext } from "./context";
import { useAuthSettings } from "./hooks";
import { toSameOriginPath } from "./returnUrl";

export function TLPCloudLoginPage() {
	const { rememberMe, setRememberMe, username, setUsername, returnUrl, setReturnUrl, clearStorage } =
		useContext(AuthContext);
	const [password, setPassword] = useState<string>("");
	const [disabled, setDisabled] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [loggingIn, setLoggingIn] = useState<boolean>(false);
	const history = useHistory();
	const params = useSearchParams();
	const authSettingsQuery = useAuthSettings();

	useEffect(() => {
		document.title = "TLP Cloud - Login";
	}, []);

	useEffect(() => {
		if (params.get("ReturnUrl")) {
			setReturnUrl(params.get("ReturnUrl"));
		}
	}, [params, setReturnUrl]);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!username || !password) return;
		setDisabled(true);
		setLoggingIn(true);
		await login();
	}

	function errorHandler(message: string, error?: any) {
		log(error, "error");
		setError(message);
		setDisabled(false);
		setLoggingIn(false);
	}

	async function login() {
		try {
			const response = await AccountController.login(username, password);

			await handleResponse(response);
		} catch (error) {
			errorHandler("An error occurred while logging in", error);
		}

		async function handleResponse(response: LoginResultMVC) {
			if (!response.success) {
				errorHandler(response.message ?? "Invalid username or password");
			} else if (response.mustChangePassword) {
				history.push("/ResetPassword");
			} else if (response.mfaRequired) {
				await requestMfaCode(response);
			} else {
				if (!rememberMe) clearStorage();
				window.location.replace(toSameOriginPath(returnUrl) ?? response.returnUrl);
			}
		}

		async function requestMfaCode(response: LoginResultMVC) {
			if (!response.mfaOptions?.length) {
				errorHandler("No MFA options found");
			} else if (response.mfaOptions?.length === 1) {
				const mfaResponse = await AccountController.requestMfaCode(username, response.mfaOptions[0].id);
				if (!mfaResponse.success) {
					errorHandler(mfaResponse.message ?? "Failed to request MFA code");
				} else {
					history.push("/OTP");
				}
			} else {
				history.push("/MfaType");
			}
		}
	}

	function forgotPassword() {
		history.push("/ForgotPassword");
	}

	return (
		<LoginPage
			username={username}
			setUsername={setUsername}
			password={password}
			setPassword={setPassword}
			submitFunction={submit}
			forgotPassword={forgotPassword}
			rememberMe={rememberMe}
			setRememberMe={setRememberMe}
			disabled={disabled}
			loading={loggingIn}
			error={error}
			backgroundImage={authSettingsQuery.data?.backgroundImage}
			appLogo={authSettingsQuery.data?.companyLogo}
		/>
	);
}
